import { authorizeVerificationApiRequest, } from "./api-auth.js";
export const VERIFICATION_API_CATALOG_VERSION = "2026-08-16.v1";
export const VERIFICATION_API_BASE_PATH = "/api/verification/v1";
/**
 * The digest the request examples register and then cite.
 *
 * Reproducible on purpose: `printf 'excellent-evidence-example' | shasum -a 256`.
 * A reader can derive it from bytes they hold rather than copying an opaque
 * blob, and the same value threads `evidence.create` → `claims.create` so the
 * two examples read as one walkthrough.
 */
const EXAMPLE_CONTENT_DIGEST = "sha256:fdc8462e8a2e5e6b5b1d66daa92ed6ffa65373ea32a1eade78068d2d4454de2f";
const STANDARD_ERRORS = [
    "bad_request",
    "forbidden",
    "not_found",
    "rate_limited",
    "tenant_mismatch",
    "unauthorized",
];
const WRITE_ERRORS = [
    ...STANDARD_ERRORS,
    "conflict",
    "unprocessable_entity",
];
const READ_LIMIT = {
    windowSeconds: 60,
    maxRequests: 600,
    scope: "token",
};
const WRITE_LIMIT = {
    windowSeconds: 60,
    maxRequests: 120,
    scope: "token",
};
function scopeFor(resource, action) {
    return `${resource}:${action}`;
}
function endpoint(input) {
    return {
        ...input,
        version: VERIFICATION_API_CATALOG_VERSION,
        requiredScope: scopeFor(input.resource, input.action),
    };
}
export const VERIFICATION_API_ENDPOINTS = [
    endpoint({
        id: "claims.list",
        stability: "stable",
        method: "GET",
        path: "/api/verification/v1/claims",
        resource: "claims",
        action: "read",
        summary: "List submitted verification claims, filtered by the subject a claim is about (`subjectRef`).",
        requestFieldsIn: "query",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "verification.claims.list",
            requestExample: { subjectRef: "artifact:attestation" },
        },
        errors: STANDARD_ERRORS,
        rateLimit: READ_LIMIT,
    }),
    endpoint({
        id: "claims.create",
        stability: "stable",
        method: "POST",
        path: "/api/verification/v1/claims",
        resource: "claims",
        action: "write",
        summary: "Submit a claim to be checked by deterministic, semantic, or human review steps.",
        requestFieldsIn: "body",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "verification.claims.create",
            // `predicate` stays exactly what it was: an opaque human label, which no
            // evaluator can read, so a claim carrying only it resolves UNKNOWN.
            // `typedPredicate` is ADDITIVE and optional — supply it and the claim is
            // compiled, refused at intake if it cannot be refuted, and decided by the
            // shared deterministic evaluator named in its obligation.
            requestExample: {
                subjectRef: "artifact:attestation",
                predicate: "the cited attestation is the one we pinned",
                typedPredicate: {
                    kind: "row",
                    tier: 1,
                    signature: {
                        symbols: [
                            {
                                name: "d",
                                sort: "String",
                                unit: null,
                                meaning: "the registered content digest of the cited artifact",
                                span: "evidence.content-digest",
                            },
                        ],
                    },
                    // A REAL digest, not `sha256:<64 lowercase hex>`. The placeholder was
                    // refused at intake (`bind:predicate-domain-unsatisfiable`): the
                    // compiler binds the expression against the observable's domain, and
                    // an angle-bracket placeholder is not a member of it. So the one
                    // published example of the product's own typed-predicate shape was a
                    // 400. It is the SAME digest `evidence.create` registers above, which
                    // makes the two examples one walkthrough rather than two fragments.
                    expr: `(= d "${EXAMPLE_CONTENT_DIGEST}")`,
                    falsifier: `(not (= d "${EXAMPLE_CONTENT_DIGEST}"))`,
                    sourceSpan: "the cited attestation has the pinned content digest",
                    observable: "evidence.content-digest",
                    evidenceKind: "attestation",
                },
                evidenceArtifactIds: ["ev_123"],
            },
        },
        errors: WRITE_ERRORS,
        rateLimit: WRITE_LIMIT,
    }),
    endpoint({
        id: "work.list",
        stability: "stable",
        method: "GET",
        path: "/api/verification/v1/work",
        resource: "work",
        action: "read",
        summary: "List canonical work items, filtered by the caller's own external reference (`externalRef`).",
        requestFieldsIn: "query",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "verification.work.list",
            requestExample: { externalRef: "ticket-123" },
        },
        errors: STANDARD_ERRORS,
        rateLimit: READ_LIMIT,
    }),
    endpoint({
        id: "work.create",
        stability: "stable",
        method: "POST",
        path: "/api/verification/v1/work",
        resource: "work",
        action: "write",
        summary: "Create canonical Work from an external client without Excellent task identifiers.",
        requestFieldsIn: "body",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "verification.work.create",
            requestExample: { externalRef: "ticket-123", objective: "Verify SOC2 MFA evidence" },
        },
        errors: WRITE_ERRORS,
        rateLimit: WRITE_LIMIT,
    }),
    endpoint({
        id: "evidence.list",
        stability: "stable",
        method: "GET",
        path: "/api/verification/v1/evidence",
        resource: "evidence",
        action: "read",
        summary: "List immutable evidence artifact metadata, filtered by `sourceKind` and `sourceId`.",
        requestFieldsIn: "query",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "verification.evidence.list",
            requestExample: { sourceKind: "idp", sourceId: "conn_idp" },
        },
        errors: STANDARD_ERRORS,
        rateLimit: READ_LIMIT,
    }),
    endpoint({
        id: "evidence.create",
        stability: "stable",
        method: "POST",
        path: "/api/verification/v1/evidence",
        resource: "evidence",
        action: "write",
        summary: "Register a content-addressed evidence artifact and its collector/source metadata.",
        requestFieldsIn: "body",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "verification.evidence.create",
            // `contentHash` is the engine's ADDRESS for the artifact, not a label:
            // `registerEvidence` refuses anything that is not `sha256:<64 lowercase
            // hex>`, because a hash it cannot later re-verify makes the run-time
            // integrity check vacuous. This digest is real and reproducible —
            // `printf 'excellent-evidence-example' | shasum -a 256` — so a reader who
            // pastes this example gets a 201 rather than a 400, and can see how the
            // value is derived from bytes they hold.
            requestExample: {
                sourceId: "conn_idp",
                contentHash: EXAMPLE_CONTENT_DIGEST,
                sourceKind: "idp",
            },
        },
        errors: WRITE_ERRORS,
        rateLimit: WRITE_LIMIT,
    }),
    endpoint({
        id: "verify.status",
        stability: "stable",
        method: "GET",
        path: "/api/verification/v1/verify/{verificationId}",
        resource: "verify",
        action: "read",
        summary: "Read async verification status, canonical ids, and current determination state.",
        requestFieldsIn: "path",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "verification.verify.status",
            requestExample: { verificationId: "ver_123" },
        },
        errors: STANDARD_ERRORS,
        rateLimit: READ_LIMIT,
    }),
    endpoint({
        id: "verify.start",
        stability: "stable",
        method: "POST",
        path: "/api/verification/v1/verify",
        resource: "verify",
        action: "write",
        summary: "Start an async verification against Work, policies, evidence, and gates.",
        requestFieldsIn: "body",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "verification.verify.start",
            requestExample: { workId: "work_123", policyIds: ["pol_mfa_v1"] },
        },
        errors: WRITE_ERRORS,
        rateLimit: WRITE_LIMIT,
    }),
    endpoint({
        id: "gates.list",
        stability: "stable",
        method: "GET",
        path: "/api/verification/v1/gates",
        resource: "gates",
        action: "read",
        summary: "List recorded gate runs, filtered by the work item each was requested for (`workId`).",
        requestFieldsIn: "query",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "verification.gates.list",
            requestExample: { workId: "work_123" },
        },
        errors: STANDARD_ERRORS,
        rateLimit: READ_LIMIT,
    }),
    endpoint({
        id: "gates.run",
        stability: "stable",
        method: "POST",
        path: "/api/verification/v1/gates",
        resource: "gates",
        action: "write",
        summary: "Request a canonical gate run with idempotent async status and receipt linkage.",
        requestFieldsIn: "body",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "verification.gates.run",
            requestExample: { workId: "work_123", command: "pnpm test-verification-api-sdk" },
        },
        errors: WRITE_ERRORS,
        rateLimit: WRITE_LIMIT,
    }),
    endpoint({
        id: "policies.list",
        stability: "stable",
        method: "GET",
        path: "/api/verification/v1/policies",
        resource: "policies",
        action: "read",
        summary: "List policy activations, filtered by the policy each activated (`policyId`).",
        requestFieldsIn: "query",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "verification.policies.list",
            requestExample: { policyId: "pol_123" },
        },
        errors: STANDARD_ERRORS,
        rateLimit: READ_LIMIT,
    }),
    endpoint({
        id: "policies.activate",
        stability: "beta",
        method: "POST",
        path: "/api/verification/v1/policies/{policyId}/activate",
        resource: "policies",
        action: "write",
        summary: "Activate a policy version after impact analysis and human authorization.",
        requestFieldsIn: "body",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "verification.policies.activate",
            requestExample: { policyId: "pol_123", rationale: "Quarterly control update" },
        },
        errors: WRITE_ERRORS,
        rateLimit: WRITE_LIMIT,
    }),
    endpoint({
        id: "reviews.list",
        stability: "stable",
        method: "GET",
        path: "/api/verification/v1/reviews",
        resource: "reviews",
        action: "read",
        summary: "List review decisions, filtered by `workId` and `decision`.",
        requestFieldsIn: "query",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "verification.reviews.list",
            requestExample: { workId: "work_123", decision: "approved" },
        },
        errors: STANDARD_ERRORS,
        rateLimit: READ_LIMIT,
    }),
    endpoint({
        id: "reviews.create",
        stability: "stable",
        method: "POST",
        path: "/api/verification/v1/reviews",
        resource: "reviews",
        action: "write",
        summary: "Record an independent review decision over canonical Work and evidence.",
        requestFieldsIn: "body",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "verification.reviews.create",
            requestExample: {
                workId: "work_123",
                decision: "approved",
                rationale: "Gate receipts match",
            },
        },
        errors: WRITE_ERRORS,
        rateLimit: WRITE_LIMIT,
    }),
    endpoint({
        id: "runs.list",
        stability: "stable",
        method: "GET",
        path: "/api/verification/v1/runs",
        resource: "runs",
        action: "read",
        summary: "List verification runs, filtered by the claim each ran over (`claimId`).",
        requestFieldsIn: "query",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "verification.runs.list",
            requestExample: { claimId: "clm_123" },
        },
        errors: STANDARD_ERRORS,
        rateLimit: READ_LIMIT,
    }),
    endpoint({
        id: "runs.create",
        stability: "stable",
        method: "POST",
        path: "/api/verification/v1/runs",
        resource: "runs",
        action: "write",
        summary: "Create a verification run from controls, policies, and evidence artifacts.",
        requestFieldsIn: "body",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "verification.runs.create",
            // ONE RUN DECIDES ONE CLAIM, and `claimId` is the only field the handler
            // reads. The example published here until 2026-09-13 was
            // `{ controlIds, policyIds, evidenceArtifactIds }` — three keys the
            // handler then discarded without a word, and no `claimId`, so copying it
            // produced `400 claimId must be a non-empty string`. Controls, policies
            // and evidence reach a run through the CLAIM (`claims.create`), which is
            // where `evidenceArtifactIds` actually belongs — and sending them here now
            // ANSWERS with that fact (`400 unsupported_body_field`) instead of
            // dropping them silently.
            requestExample: { claimId: "clm_123" },
        },
        errors: WRITE_ERRORS,
        rateLimit: WRITE_LIMIT,
    }),
    endpoint({
        id: "findings.list",
        stability: "stable",
        method: "GET",
        path: "/api/verification/v1/findings",
        resource: "findings",
        action: "read",
        summary: "List recorded finding reviews, filtered by `findingId` and `decision`.",
        requestFieldsIn: "query",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "verification.findings.list",
            requestExample: { findingId: "find_123", decision: "request_evidence" },
        },
        errors: STANDARD_ERRORS,
        rateLimit: READ_LIMIT,
    }),
    endpoint({
        id: "findings.review",
        stability: "stable",
        method: "POST",
        path: "/api/verification/v1/findings/{findingId}/review-decisions",
        resource: "findings",
        action: "write",
        summary: "Record a reviewer decision with rationale, evidence refs, and policy version.",
        requestFieldsIn: "body",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "verification.findings.review",
            requestExample: {
                findingId: "find_123",
                decision: "request_evidence",
                rationale: "Missing population export",
            },
        },
        errors: WRITE_ERRORS,
        rateLimit: WRITE_LIMIT,
    }),
    endpoint({
        id: "receipts.get",
        stability: "stable",
        method: "GET",
        path: "/api/verification/v1/receipts/{receiptId}",
        resource: "receipts",
        action: "read",
        summary: "Read a receipt with signature metadata, evidence hashes, and replay context.",
        requestFieldsIn: "path",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "verification.receipts.get",
            requestExample: { receiptId: "rcpt_123" },
        },
        errors: STANDARD_ERRORS,
        rateLimit: READ_LIMIT,
    }),
    endpoint({
        id: "receipts.issue",
        stability: "stable",
        method: "POST",
        path: "/api/verification/v1/runs/{runId}/receipts",
        resource: "receipts",
        action: "write",
        summary: "Issue a receipt only when deterministic/human-supported checks and evidence hashes cover the run.",
        requestFieldsIn: "body",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "verification.receipts.issue",
            requestExample: { runId: "run_123", keyId: "key_2026_08" },
        },
        errors: WRITE_ERRORS,
        rateLimit: WRITE_LIMIT,
    }),
    endpoint({
        id: "outcomes.list",
        stability: "stable",
        method: "GET",
        path: "/api/verification/v1/outcomes",
        resource: "outcomes",
        action: "read",
        summary: "List determinations and business outcomes, filtered by `workId` and `verdict`.",
        requestFieldsIn: "query",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "verification.outcomes.list",
            requestExample: { workId: "work_123", verdict: "PASS" },
        },
        errors: STANDARD_ERRORS,
        rateLimit: READ_LIMIT,
    }),
    endpoint({
        id: "outcomes.create",
        stability: "stable",
        method: "POST",
        path: "/api/verification/v1/outcomes",
        resource: "outcomes",
        action: "write",
        summary: "Record a canonical outcome bound to Work, run, receipt, and review identifiers.",
        requestFieldsIn: "body",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "verification.outcomes.create",
            requestExample: { workId: "work_123", runId: "run_123", verdict: "PASS" },
        },
        errors: WRITE_ERRORS,
        rateLimit: WRITE_LIMIT,
    }),
    endpoint({
        id: "autonomy.rules.list",
        stability: "stable",
        method: "GET",
        path: "/api/verification/v1/autonomy/rules",
        resource: "autonomy",
        action: "read",
        summary: "List scoped autonomy rules, filtered by `ruleId`, `enabled`, and `maxConsequence`.",
        requestFieldsIn: "query",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "verification.autonomy.rules.list",
            requestExample: { ruleId: "auto_low_risk", enabled: true, maxConsequence: "low" },
        },
        errors: STANDARD_ERRORS,
        rateLimit: READ_LIMIT,
    }),
    endpoint({
        id: "autonomy.rules.upsert",
        stability: "stable",
        method: "POST",
        path: "/api/verification/v1/autonomy/rules",
        resource: "autonomy",
        action: "write",
        summary: "Create or update a scoped autonomy rule with enforcement and audit linkage.",
        requestFieldsIn: "body",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "verification.autonomy.rules.upsert",
            requestExample: { ruleId: "auto_low_risk", enabled: true, maxConsequence: "low" },
        },
        errors: WRITE_ERRORS,
        rateLimit: WRITE_LIMIT,
    }),
    endpoint({
        id: "audit.events.list",
        stability: "stable",
        method: "GET",
        path: "/api/verification/v1/audit/events",
        resource: "audit",
        action: "read",
        summary: "List immutable audit events, filtered by `subjectId` and `kind`.",
        requestFieldsIn: "query",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "verification.audit.events.list",
            requestExample: { subjectId: "work_123", kind: "external.client.submitted" },
        },
        errors: STANDARD_ERRORS,
        rateLimit: READ_LIMIT,
    }),
    endpoint({
        id: "audit.events.create",
        stability: "stable",
        method: "POST",
        path: "/api/verification/v1/audit/events",
        resource: "audit",
        action: "write",
        summary: "Append an external audit event with idempotent provenance and subject linkage.",
        requestFieldsIn: "body",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "verification.audit.events.create",
            requestExample: { subjectId: "work_123", kind: "external.client.submitted" },
        },
        errors: WRITE_ERRORS,
        rateLimit: WRITE_LIMIT,
    }),
    endpoint({
        id: "integrations.list",
        stability: "stable",
        method: "GET",
        path: "/api/verification/v1/integrations",
        resource: "integrations",
        action: "read",
        /**
         * NO FILTERS, because there is nothing here to filter.
         *
         * The only operation that could put a row in this collection is
         * `integrations.sync`, which is declared unimplemented and refuses 501:
         * this deployment has no connector collector. So the list is permanently
         * empty, and `?sourceKind=idp&status=connected` — which it advertised
         * until now — promised a caller they could narrow a set that cannot have
         * members. Filters on a permanently empty list are a false publication
         * twice over. Either sent parameter is now refused by name.
         */
        summary: "List connected source systems. Takes no filters: nothing in this deployment connects one.",
        requestFieldsIn: "none",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "verification.integrations.list",
            requestExample: {},
        },
        errors: STANDARD_ERRORS,
        rateLimit: READ_LIMIT,
    }),
    endpoint({
        id: "integrations.sync",
        stability: "stable",
        method: "POST",
        path: "/api/verification/v1/integrations/{integrationId}/sync",
        resource: "integrations",
        action: "write",
        summary: "Request an idempotent evidence sync from a connected source system.",
        requestFieldsIn: "body",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "verification.integrations.sync",
            requestExample: { integrationId: "conn_idp", reason: "scheduled_control_run" },
        },
        errors: WRITE_ERRORS,
        rateLimit: WRITE_LIMIT,
    }),
    endpoint({
        id: "conformance.results.list",
        stability: "stable",
        method: "GET",
        path: "/api/verification/v1/conformance/results",
        resource: "conformance",
        action: "read",
        /**
         * NO FILTERS, for the same reason as `integrations.list`: `conformance.run`
         * is declared unimplemented, this deployment carries no scenario registry
         * and no results store, and its own refusal says in so many words that
         * "conformance.results.list has nothing to return". A `?profile=` filter
         * over nothing described a capability twice removed from what exists.
         */
        summary: "List conformance results. Takes no filters: this deployment runs no conformance scenarios.",
        requestFieldsIn: "none",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "verification.conformance.results.list",
            requestExample: {},
        },
        errors: STANDARD_ERRORS,
        rateLimit: READ_LIMIT,
    }),
    endpoint({
        id: "conformance.run",
        stability: "stable",
        method: "POST",
        path: "/api/verification/v1/conformance/runs",
        resource: "conformance",
        action: "write",
        summary: "Start a conformance run for profile parity, semantic parity, and bypass-negative proof.",
        requestFieldsIn: "body",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "verification.conformance.run",
            requestExample: { profile: "production-like", scenarioIds: ["FLOW-001"] },
        },
        errors: WRITE_ERRORS,
        rateLimit: WRITE_LIMIT,
    }),
    endpoint({
        id: "exports.create",
        stability: "stable",
        method: "POST",
        path: "/api/verification/v1/exports",
        resource: "exports",
        action: "write",
        summary: "Create a scoped export packet for audit-room, GRC exchange, or public verification use.",
        requestFieldsIn: "body",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "verification.exports.create",
            requestExample: { runIds: ["run_123"], redactionRefs: ["artifact:ev_secret"] },
        },
        errors: WRITE_ERRORS,
        rateLimit: WRITE_LIMIT,
    }),
    endpoint({
        id: "exports.get",
        stability: "stable",
        method: "GET",
        path: "/api/verification/v1/exports/{exportId}",
        resource: "exports",
        action: "read",
        summary: "Read a scoped export packet with receipt ids, redactions, expiry, and audit-log impact.",
        requestFieldsIn: "path",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "verification.exports.get",
            requestExample: { exportId: "exp_123" },
        },
        errors: STANDARD_ERRORS,
        rateLimit: READ_LIMIT,
    }),
];
export const ENVIRONMENT_VERIFICATION_API_ENDPOINTS = [
    endpoint({
        id: "environments.list",
        stability: "beta",
        method: "GET",
        path: "/api/environments",
        resource: "environments",
        action: "read",
        /**
         * NO QUERY FILTERS — found by the same sweep that closed the served surface.
         *
         * This entry published `?lifecycleState=active&trustState=trusted`. Measured
         * 2026-09-13: `trustState` occurs as a REQUEST parameter in exactly one
         * place in the tree — the example this replaces. Everywhere else it is a
         * FIELD of an Environment projection (`environments/schema.ts`,
         * `storage/artifacts.ts`, `surfaces/public-runtime.ts`). Neither name is
         * read as a filter by anything.
         *
         * It is also worse off than the thirteen served operations were, not
         * better: `ENVIRONMENT_VERIFICATION_API_ENDPOINTS` has NO dispatcher at all
         * — its only consumer is `api-sdk.ts`, which builds request plans. So the
         * SDK was emitting a query string toward an operation nothing in this repo
         * serves and nothing anywhere filters on.
         *
         * The filter names are kept HERE, in prose, because they are the plausible
         * future contract and a reader should not have to rediscover them. What is
         * removed is the PUBLICATION of them as a capability. Implementing the
         * filter means restoring all three declarations together — summary,
         * example, `requestFieldsIn` — and `check-verification-api-request-examples`
         * L5/L6 will then hold them to it, as it does for the served lists.
         */
        summary: "List signed Environment directory projections visible to the caller. Takes no filters: this catalogue entry has no dispatcher, and nothing reads a lifecycle or trust state as a query parameter.",
        requestFieldsIn: "none",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "excellent.environments.environments.list",
            requestExample: {},
        },
        errors: STANDARD_ERRORS,
        rateLimit: READ_LIMIT,
    }),
    endpoint({
        id: "environment.detail",
        stability: "beta",
        method: "GET",
        path: "/api/environments/{environmentId}",
        resource: "environments",
        action: "read",
        summary: "Read the signed current Environment directory projection for one Environment.",
        requestFieldsIn: "path",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "excellent.environments.environment.detail",
            requestExample: { environmentId: "env_prod" },
        },
        errors: STANDARD_ERRORS,
        rateLimit: READ_LIMIT,
    }),
    endpoint({
        id: "environment.health",
        stability: "beta",
        method: "GET",
        path: "/api/environments/{environmentId}/health",
        resource: "environments",
        action: "read",
        summary: "Read canonical Environment health for the current generation.",
        requestFieldsIn: "path",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "excellent.environments.environment.health",
            requestExample: { environmentId: "env_prod" },
        },
        errors: STANDARD_ERRORS,
        rateLimit: READ_LIMIT,
    }),
    endpoint({
        id: "environment.capabilities",
        stability: "beta",
        method: "GET",
        path: "/api/environments/{environmentId}/capabilities",
        resource: "environments",
        action: "read",
        summary: "Read canonical Environment capabilities for the current generation.",
        requestFieldsIn: "path",
        responseSchemaId: null,
        sdk: {
            language: "typescript",
            functionName: "excellent.environments.environment.capabilities",
            requestExample: { environmentId: "env_prod" },
        },
        errors: STANDARD_ERRORS,
        rateLimit: READ_LIMIT,
    }),
    endpoint({
        id: "environment.current-resource-grant.status",
        stability: "beta",
        method: "GET",
        path: "/api/environments/{environmentId}/resource-grant-status",
        resource: "environments",
        action: "read",
        summary: "Read the host-bound current resource grant status for an Environment without granting new authority.",
        requestFieldsIn: "path",
        responseSchemaId: "excellent.environment.current-resource-grant.status.v1",
        sdk: {
            language: "typescript",
            functionName: "excellent.environments.environment.currentResourceGrantStatus",
            requestExample: { environmentId: "env_prod" },
        },
        errors: STANDARD_ERRORS,
        rateLimit: READ_LIMIT,
    }),
    endpoint({
        id: "environment.collector-host.status",
        stability: "beta",
        method: "GET",
        path: "/api/environments/{environmentId}/collector-host-status",
        resource: "environments",
        action: "read",
        summary: "Read the independent collector host status, custody destination, and bounded-buffer policy for an Environment.",
        requestFieldsIn: "path",
        responseSchemaId: "excellent.environment.collector-host.status.v1",
        sdk: {
            language: "typescript",
            functionName: "excellent.environments.environment.collectorHostStatus",
            requestExample: { environmentId: "env_prod" },
        },
        errors: STANDARD_ERRORS,
        rateLimit: READ_LIMIT,
    }),
    endpoint({
        id: "environment.operations.status",
        stability: "beta",
        method: "GET",
        path: "/api/operations/{operationId}",
        resource: "environments",
        action: "read",
        summary: "Read a canonical Environment operation handle.",
        requestFieldsIn: "path",
        responseSchemaId: "excellent.environment.operation-handle.v1",
        sdk: {
            language: "typescript",
            functionName: "excellent.environments.operations.status",
            requestExample: { operationId: "sha256:operation" },
        },
        errors: STANDARD_ERRORS,
        rateLimit: READ_LIMIT,
    }),
    endpoint({
        id: "environment.operations.watch",
        stability: "beta",
        method: "GET",
        path: "/api/operations/{operationId}/events",
        resource: "environments",
        action: "read",
        summary: "Read canonical Environment operation events.",
        requestFieldsIn: "path",
        responseSchemaId: "excellent.environment.operation-events.v1",
        sdk: {
            language: "typescript",
            functionName: "excellent.environments.operations.watch",
            requestExample: { operationId: "sha256:operation" },
        },
        errors: STANDARD_ERRORS,
        rateLimit: READ_LIMIT,
    }),
    endpoint({
        id: "environment.operations.cancel",
        stability: "beta",
        method: "POST",
        path: "/api/operations/{operationId}/cancel",
        resource: "environments",
        action: "write",
        summary: "Request an idempotent Environment operation cancellation.",
        requestFieldsIn: "body",
        responseSchemaId: "excellent.environment.operation-handle.v1",
        sdk: {
            language: "typescript",
            functionName: "excellent.environments.operations.cancel",
            requestExample: { operationId: "sha256:operation", reason: "operator_requested" },
        },
        errors: WRITE_ERRORS,
        rateLimit: WRITE_LIMIT,
    }),
    endpoint({
        id: "environment.operations.resume",
        stability: "beta",
        method: "POST",
        path: "/api/operations/{operationId}/resume",
        resource: "environments",
        action: "write",
        summary: "Request idempotent resume of a paused or interrupted Environment operation.",
        requestFieldsIn: "body",
        responseSchemaId: "excellent.environment.operation-handle.v1",
        sdk: {
            language: "typescript",
            functionName: "excellent.environments.operations.resume",
            requestExample: {
                operationId: "sha256:operation",
                originalExecutionIdempotencyKey: "env-op-key",
            },
        },
        errors: WRITE_ERRORS,
        rateLimit: WRITE_LIMIT,
    }),
    endpoint({
        id: "environment.operations.reconcile",
        stability: "beta",
        method: "POST",
        path: "/api/operations/{operationId}/reconcile",
        resource: "environments",
        action: "write",
        summary: "Request idempotent reconciliation of an Environment operation with uncertain provider state.",
        requestFieldsIn: "body",
        responseSchemaId: "excellent.environment.operation-handle.v1",
        sdk: {
            language: "typescript",
            functionName: "excellent.environments.operations.reconcile",
            requestExample: {
                operationId: "sha256:operation",
                authorizationId: "auth_123",
                canonicalSignedAuthorizationBytes: "{}",
                canonicalSignedTransitionBytes: "{}",
                canonicalSignedTerminalEffectBytes: "{}",
            },
        },
        errors: WRITE_ERRORS,
        rateLimit: WRITE_LIMIT,
    }),
    endpoint({
        id: "environment.provider-connections.list",
        stability: "beta",
        method: "GET",
        path: "/api/provider-connections",
        resource: "environments",
        action: "read",
        summary: "List provider connections through the bound Environment provider lifecycle surface.",
        requestFieldsIn: "none",
        responseSchemaId: "excellent.environment.provider-connection.lifecycle-surface.v1",
        sdk: {
            language: "typescript",
            functionName: "excellent.environments.providerConnections.list",
            requestExample: {},
        },
        errors: STANDARD_ERRORS,
        rateLimit: READ_LIMIT,
    }),
    endpoint({
        id: "environment.provider-connection.authorize.plan",
        stability: "beta",
        method: "POST",
        path: "/api/provider-connections/authorization-plans",
        resource: "environments",
        action: "write",
        summary: "Plan provider-connection authorization through the bound Environment lifecycle surface.",
        requestFieldsIn: "body",
        responseSchemaId: "excellent.environment.provider-connection.lifecycle-surface.v1",
        sdk: {
            language: "typescript",
            functionName: "excellent.environments.providerConnections.authorizationPlan",
            requestExample: { provider: "digitalocean", idempotencyKey: "provider-authorize-001" },
        },
        errors: WRITE_ERRORS,
        rateLimit: WRITE_LIMIT,
    }),
    endpoint({
        id: "environment.provider-connection.plan.execute",
        stability: "beta",
        method: "POST",
        path: "/api/provider-connection-plans/{planId}/execute",
        resource: "environments",
        action: "write",
        summary: "Execute an approved provider-connection plan through the bound Environment lifecycle surface.",
        requestFieldsIn: "body",
        responseSchemaId: "excellent.environment.provider-connection.lifecycle-surface.v1",
        sdk: {
            language: "typescript",
            functionName: "excellent.environments.providerConnections.executePlan",
            requestExample: { planId: "plan_123", idempotencyKey: "provider-execute-001" },
        },
        errors: WRITE_ERRORS,
        rateLimit: WRITE_LIMIT,
    }),
    endpoint({
        id: "environment.provider-connection.detail",
        stability: "beta",
        method: "GET",
        path: "/api/provider-connections/{connectionId}",
        resource: "environments",
        action: "read",
        summary: "Read provider-connection detail through the bound Environment lifecycle surface.",
        requestFieldsIn: "path",
        responseSchemaId: "excellent.environment.provider-connection.lifecycle-surface.v1",
        sdk: {
            language: "typescript",
            functionName: "excellent.environments.providerConnections.detail",
            requestExample: { connectionId: "provider_connection_123" },
        },
        errors: STANDARD_ERRORS,
        rateLimit: READ_LIMIT,
    }),
    endpoint({
        id: "environment.provider-connection.reauthorize.plan",
        stability: "beta",
        method: "POST",
        path: "/api/provider-connections/{connectionId}/reauthorization-plans",
        resource: "environments",
        action: "write",
        summary: "Plan provider-connection reauthorization through the bound Environment lifecycle surface.",
        requestFieldsIn: "body",
        responseSchemaId: "excellent.environment.provider-connection.lifecycle-surface.v1",
        sdk: {
            language: "typescript",
            functionName: "excellent.environments.providerConnections.reauthorizationPlan",
            requestExample: {
                connectionId: "provider_connection_123",
                idempotencyKey: "provider-reauthorize-001",
            },
        },
        errors: WRITE_ERRORS,
        rateLimit: WRITE_LIMIT,
    }),
    endpoint({
        id: "environment.provider-connection.revoke.plan",
        stability: "beta",
        method: "POST",
        path: "/api/provider-connections/{connectionId}/revocation-plans",
        resource: "environments",
        action: "write",
        summary: "Plan provider-connection revocation through the bound Environment lifecycle surface.",
        requestFieldsIn: "body",
        responseSchemaId: "excellent.environment.provider-connection.lifecycle-surface.v1",
        sdk: {
            language: "typescript",
            functionName: "excellent.environments.providerConnections.revocationPlan",
            requestExample: {
                connectionId: "provider_connection_123",
                idempotencyKey: "provider-revoke-001",
            },
        },
        errors: WRITE_ERRORS,
        rateLimit: WRITE_LIMIT,
    }),
    endpoint({
        id: "environment.provider-connection.inventory-refresh",
        stability: "beta",
        method: "POST",
        path: "/api/provider-connections/{connectionId}/inventory-refresh",
        resource: "environments",
        action: "write",
        summary: "Refresh provider inventory through the bound Environment provider lifecycle surface.",
        requestFieldsIn: "body",
        responseSchemaId: "excellent.environment.provider-connection.lifecycle-surface.v1",
        sdk: {
            language: "typescript",
            functionName: "excellent.environments.providerConnections.inventoryRefresh",
            requestExample: {
                connectionId: "provider_connection_123",
                idempotencyKey: "provider-inventory-001",
            },
        },
        errors: WRITE_ERRORS,
        rateLimit: WRITE_LIMIT,
    }),
    endpoint({
        id: "environment.provider-connection.expired-claim.refuse",
        stability: "beta",
        method: "POST",
        path: "/api/provider-connections/{connectionId}/expired-claim-refusals",
        resource: "environments",
        action: "write",
        summary: "Expose expired provider capability-claim refusal through the bound Environment lifecycle surface.",
        requestFieldsIn: "body",
        responseSchemaId: "excellent.environment.provider-connection.lifecycle-surface.v1",
        sdk: {
            language: "typescript",
            functionName: "excellent.environments.providerConnections.expiredClaimRefusal",
            requestExample: {
                connectionId: "provider_connection_123",
                providerCapabilityClaimDigest: "sha256:eeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
                idempotencyKey: "provider-expired-claim-refusal-001",
            },
        },
        errors: WRITE_ERRORS,
        rateLimit: WRITE_LIMIT,
    }),
];
export const VERIFICATION_SDK_CATALOG_ENDPOINTS = [
    ...VERIFICATION_API_ENDPOINTS,
    ...ENVIRONMENT_VERIFICATION_API_ENDPOINTS,
];
/* ── Declared, not implemented ───────────────────────────────────────────── */
/**
 * Operations this catalogue ADVERTISES and this product will not PERFORM.
 *
 * ── Why this exists ──
 *
 * `integrations.sync` and `conformance.run` refuse with 501 `not_implemented`
 * (`api-handlers.ts`), deliberately: each formerly answered 202 and wrote a row
 * that read `queued` for the life of the database because nothing dequeued it
 * (e95aa1ab8). Refusing beats pretending. But until this declaration existed,
 * the catalogue described those two exactly as it described every operation
 * that works — same `stability: "stable"`, same error model, an SDK example
 * that will never succeed — so the only way a caller could learn the truth was
 * to send the request. A verification product that advertises a capability
 * indistinguishably from the ones it performs is making the class of claim this
 * product exists to refuse.
 *
 * So the refusal is now DECLARED where the advertisement is. 501 for an
 * operation named here is the correct and required answer; 501 for an operation
 * NOT named here is a defect — an advertised capability silently gone dark.
 * `test-verification-ws10-adapters` asserts exactly that biconditional over the
 * whole public surface, which is why it reads this map rather than carrying a
 * hand-maintained list of ids permitted to refuse: a third operation going dark
 * has to change this file to stay green.
 *
 * Sibling of `ENGINE_ONLY_OPERATIONS` (`api-server.ts`) and deliberately NOT
 * the same thing. Engine-only means *withheld*: removed from the public surface
 * and refused before authorization, because a caller must never reach it.
 * Declared-not-implemented means *reachable, documented, scoped and honest*: a
 * caller may still send the request and gets back a refusal that names the path
 * which does work. Withholding these would replace an actionable refusal with a
 * 404 and take the remedy with it.
 *
 * The value is the public one-line reason, written for a caller reading the
 * catalogue. It is not the engine's refusal message: `api-handlers.ts` authors
 * its own, interpolating request detail this file cannot see. They are two
 * independently written descriptions of one fact, which is what lets a gate
 * compare the advertisement against the behaviour instead of comparing a string
 * with itself.
 *
 * An entry here is a debt, not a design. Removing one by implementing the
 * operation is the preferred way for it to leave.
 */
export const UNIMPLEMENTED_PUBLIC_OPERATIONS = {
    "integrations.sync": "Not implemented: this deployment has no connector collector, and live vendor adapters are recorded as not-shipped with network calls forbidden. Evidence enters this API by push — POST /api/verification/v1/evidence with a content hash.",
    "conformance.run": "Not implemented: conformance is decided about a deployment by its release gates against its profile manifests, not by a tenant request at runtime. This deployment carries no scenario registry, no profile-parity runner and no conformance results store.",
};
/** Is this operation advertised by the catalogue but declared not implemented? */
export function isUnimplementedPublicOperation(operationId) {
    return Object.prototype.hasOwnProperty.call(UNIMPLEMENTED_PUBLIC_OPERATIONS, operationId);
}
export function listVerificationApiEndpoints() {
    return [...VERIFICATION_API_ENDPOINTS];
}
export function listVerificationApiResources() {
    return [...new Set(VERIFICATION_API_ENDPOINTS.map((endpoint) => endpoint.resource))].sort();
}
function endpointPathMatches(templatePath, requestPath) {
    const templateParts = templatePath.split("/");
    const requestParts = requestPath.split("/");
    if (templateParts.length !== requestParts.length)
        return false;
    return templateParts.every((part, index) => {
        const requestPart = requestParts[index];
        if (requestPart == null)
            return false;
        if (part.startsWith("{") && part.endsWith("}"))
            return requestPart.length > 0;
        return part === requestPart;
    });
}
export function findVerificationApiEndpoint(input) {
    return (VERIFICATION_API_ENDPOINTS.find((endpoint) => {
        if (endpoint.method !== input.method)
            return false;
        if (input.endpointId != null)
            return endpoint.id === input.endpointId;
        if (input.path != null)
            return endpointPathMatches(endpoint.path, input.path);
        return false;
    }) ?? null);
}
export function authorizeVerificationApiEndpoint(input) {
    const endpoint = findVerificationApiEndpoint(input);
    if (!endpoint)
        return { ok: false, reason: "endpoint-not-documented" };
    return authorizeVerificationApiRequest({
        token: input.token,
        request: {
            tenantId: input.tenantId,
            resource: endpoint.resource,
            action: endpoint.action,
            now: input.now,
        },
    });
}
export function buildVerificationSdkOperationIndex() {
    return Object.fromEntries(VERIFICATION_SDK_CATALOG_ENDPOINTS.map((endpoint) => [endpoint.sdk.functionName, endpoint]));
}
export function validateVerificationApiCatalog(endpoints = VERIFICATION_API_ENDPOINTS) {
    const issues = [];
    const endpointIds = new Set();
    const sdkOperations = new Set();
    const covered = new Set();
    for (const endpoint of endpoints) {
        if (endpointIds.has(endpoint.id)) {
            issues.push({ endpointId: endpoint.id, reason: "duplicate-endpoint-id" });
        }
        endpointIds.add(endpoint.id);
        if (sdkOperations.has(endpoint.sdk.functionName)) {
            issues.push({ endpointId: endpoint.id, reason: "duplicate-sdk-operation" });
        }
        sdkOperations.add(endpoint.sdk.functionName);
        if (!endpoint.path.startsWith(VERIFICATION_API_BASE_PATH)) {
            issues.push({ endpointId: endpoint.id, reason: "invalid-base-path" });
        }
        if (endpoint.requiredScope !== "verification:*" &&
            endpoint.requiredScope !== scopeFor(endpoint.resource, endpoint.action)) {
            issues.push({ endpointId: endpoint.id, reason: "scope-resource-action-mismatch" });
        }
        if (endpoint.errors.length === 0) {
            issues.push({ endpointId: endpoint.id, reason: "missing-error-model" });
        }
        if (endpoint.rateLimit.maxRequests <= 0 || endpoint.rateLimit.windowSeconds <= 0) {
            issues.push({ endpointId: endpoint.id, reason: "missing-rate-limit" });
        }
        covered.add(`${endpoint.resource}:${endpoint.action}`);
    }
    const requiredResources = [
        "claims",
        "work",
        "evidence",
        "verify",
        "gates",
        "policies",
        "reviews",
        "runs",
        "findings",
        "receipts",
        "outcomes",
        "autonomy",
        "audit",
        "integrations",
        "conformance",
        "exports",
    ];
    for (const resource of requiredResources) {
        for (const action of ["read", "write"]) {
            if (!covered.has(`${resource}:${action}`)) {
                issues.push({ endpointId: `${resource}:${action}`, reason: "missing-resource-action" });
            }
        }
    }
    for (const operationId of Object.keys(UNIMPLEMENTED_PUBLIC_OPERATIONS)) {
        if (!endpointIds.has(operationId)) {
            issues.push({
                endpointId: operationId,
                reason: "unimplemented-declaration-unknown-operation",
            });
        }
    }
    return issues;
}
